import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MapPin,
  Home,
  Calendar,
  User,
  Award,
  CheckCircle,
  Clock,
  Container,
  ExternalLink,
} from "lucide-react";
import { projectsData, currentProjectsData } from "../mockData";
import Footer from "../components/Footer";
import ImageLightbox from "../components/ImageLightbox";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";


const ProjectDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxImages, setLightboxImages] = useState([]);

  const zoomInUp = {
    hidden: { opacity: 0, y: 40, scale: 0.9 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.8, ease: "easeOut" } },
  };

  useEffect(() => {
    document.body.style.overflow = lightboxOpen ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [lightboxOpen]);

  const allProjects = [...projectsData, ...currentProjectsData];
  const project = allProjects.find((p) => p.id === parseInt(id));
  const isCurrentProject = project.status === "Ongoing";
  const getProjectBrochure = (projectTitle) => {
    const brochureMap = {
      "prajakta la-maison": "/images/PRAJAKTA LA MAISON_.pdf",
      "prajakta park": "/images/Prajakta-Park-E-Brochure-New-1_compressed (1)(final).pdf",
      // "prajakta classic": "/images/Prajakta Classic E-Brochure.pdf",
      // "prajakta green serenity": "/images/Green Serenity E-brochure..pdf",
      // "prajakta greenfinity": "/images/Prajakta_Greenfinity_E-brochure .pdf",
    };

    if (!projectTitle) return null;

    return brochureMap[projectTitle.toLowerCase()] || null;
  };

  const brochureURL = getProjectBrochure(project.title);

  const openLightbox = (index, images) => {
    setCurrentImageIndex(index);
    setLightboxImages(images);
    setLightboxOpen(true);
  };
  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % project.gallery.length);
  };

  const prevImage = () => {
    setCurrentImageIndex(
      (prev) => (prev - 1 + project.gallery.length) % project.gallery.length
    );
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[70vh] sm:h-[55vh] md:h-[70vh] lg:h-[90vh] overflow-hidden text-white mt-16">
        <img
          src={project.image}
          alt={project.title}
          className="absolute inset-0 w-full h-[70vh] md:h-[90vh] object-cover md:object-fill"
        />
        <div className="absolute inset-0 bg-black/30"></div>

        {/* Back Button */}
        <button
          onClick={() => {
            if (window.history.length > 2) {
              navigate(-1);
            } else {
              navigate("/projects");
            }
          }}
          className="absolute top-4 left-4 sm:top-6 sm:left-6 z-20 
             flex items-center space-x-2 text-white/80 hover:text-white 
             transition-colors duration-300 bg-black/40 px-3 py-2 my-2
             rounded-full backdrop-blur-sm"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="hidden sm:inline">Back</span>
        </button>

        {/* Text Content */}
        <div className="relative z-10 flex flex-col items-start justify-end h-full text-left px-4 sm:px-6 lg:px-12 pb-8 sm:pb-12 lg:pb-16">

          {/* Status Badge */}
          {/* <div
            className={`inline-flex items-center space-x-2 px-4 py-2 rounded-full mb-4 sm:mb-6 ${isCurrentProject ? "bg-[#facc15] text-black" : "bg-[#011321] text-white"
              }`}
          >
            {isCurrentProject ? <Clock className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
            <span className="text-sm font-semibold" style={{ fontFamily: project.fonts.tagline }}>
              {project.status}
            </span>
          </div> */}

          {/* Title */}
          <h1
            className="text-1xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 leading-tight max-w-3xl "
            style={{ fontFamily: project.fonts.title }}
          >
            <span className="text-xs sm:text-base md:text-xl lg:text-2xl
        font-bold uppercase
        text-white bg-black/60
        px-2 sm:px-3 md:px-4
        py-1.5 sm:py-2
        rounded-lg
        inline-block
        leading-tight">{project.title}</span>
          </h1>


          {/* Description */}
          {/* <p
            className="text-sm sm:text-lg md:text-xl text-slate-200 leading-relaxed max-w-2xl"
            style={{ fontFamily: project.fonts.tagline }}
          >
            {project.tagline}
          </p> */}
        </div>
      </section>

      {/* Project Overview */}
      <section className="relative py-20 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Project Overview Heading */}
          <motion.h2
            className="text-3xl sm:text-6xl font-extrabold text-[#011321] mb-12 italic"
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            Project Overview
          </motion.h2>
          <motion.div
            className="flex flex-col items-center space-y-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{
              visible: { transition: { staggerChildren: 0.5 } },
            }}
          >


            {project.fullDescription.split("\n").map((line, index) => (
              <motion.p
                key={index}
                className="text-lg sm:text-xl text-slate-900  text-left flex items-center"
                variants={Container}
                initial="hidden"
                whileInView={"visible"}
                viewport={{ once: false, amount: 0 }}
              >

                <span className="m-0 p-0 flex items-center text-justify">

                  {/*  IMAGE ONLY WHEN project.id === 2
                  {project.id === 2 && (
                    <img
                      src={project.descriptionImage}
                      alt="Project"
                      className="w-24 h-12 object-contain inline-block m-0 p-0"
                    />
                  )} */}

                  {/* TEXT */}
                  <span style={{ fontFamily: "Cinzel, serif" }} className="text-sm sm:text-base md:text-xl lg:text-2xl ">
                    {line}
                  </span>


                </span>
              </motion.p>

            ))}
          </motion.div>
        </div>
      </section>
      <div
        className="relative bg-fixed bg-center bg-cover py-16 sm:py-20 lg:py-24"
        style={{ backgroundImage: `url(${project.image})` }}
      >
        <div className="absolute inset-0 bg-black/60"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-stretch">

            {/* Project Information LEFT */}
            <motion.div
              className="bg-white/20 backdrop-blur-md rounded-3xl p-8 shadow-xl"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.3 }}
              variants={zoomInUp}
            >
              <h3 className="text-2xl font-bold text-slate-200 mb-6 text-center">Project Information</h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <MapPin className="w-6 h-6 text-slate-200 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-slate-200">Location</div>
                    <div className="text-slate-200">{project.location}</div>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <Home className="w-6 h-6 text-slate-200 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-slate-200">Type</div>
                    <div className="text-slate-200">{project.type}</div>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <Calendar className="w-6 h-6 text-slate-200 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-slate-200">
                      {isCurrentProject ? "Ongoing" : "Completed"}
                    </div>
                    <div className="text-slate-200">
                      {isCurrentProject ? project.expectedCompletion : project.completionDate}
                    </div>
                  </div>
                </div>
                {project.architecture && (
                  <div className="flex items-start space-x-4">
                    <Award className="w-6 h-6 text-slate-200 mt-1 flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-slate-200">Architecture</div>
                      <div className="text-slate-200">{project.architecture}</div>
                    </div>
                  </div>
                )}
                <div className="flex items-start space-x-4">
                  <Home className="w-6 h-6 text-slate-200 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-slate-200">NO. OF FLOORS </div>
                    <div className="text-slate-200">{project.floors}</div>
                  </div>
                </div>
                {/* {project.vastuConsultant && (
                  <div className="flex items-start space-x-4">
                    <User className="w-6 h-6 text-slate-200 mt-1 flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-slate-200">Vastu Consultant</div>
                      <div className="text-slate-200">{project.vastuConsultant}</div>
                    </div>
                  </div>
                )} */}
              </div>



              {brochureURL && (
                <div className="mt-8 pt-6 border-t border-slate-200">
                  <a
                    href={brochureURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative w-full py-4 px-6 font-bold rounded-2xl 
                 overflow-hidden transition-all duration-500 transform hover:scale-105 
                 flex items-center justify-center space-x-2"
                  >
                    <div className="absolute inset-0 bg-[#011321] group-hover:bg-[#022d57] transition-all duration-500"></div>
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent 
                      skew-x-12 -translate-x-full group-hover:translate-x-full 
                      transition-transform duration-1000"></div>
                    <div className="absolute -inset-1 bg-[#011321] rounded-2xl blur opacity-30 
                      group-hover:opacity-60 transition-opacity duration-500"></div>
                    <span className="relative z-10 text-white">Download E-Brochure</span>
                  </a>
                </div>
              )}

              {/* {[1, 2, 3, 4, 5, 6].includes(project.id) && project.videoURL && (
                <div className="mt-6">
                  <a
                    // href={project.videoURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative w-full py-4 px-6 font-bold rounded-2xl 
      overflow-hidden transition-all duration-500 transform hover:scale-105 
      flex items-center justify-center space-x-2"
                  >
                    <div className="absolute inset-0 bg-[#011321] group-hover:bg-[#022d57] transition-all duration-500"></div>
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent 
        skew-x-12 -translate-x-full group-hover:translate-x-full 
        transition-transform duration-1000"></div>
                    <div className="absolute -inset-1 bg-[#011321] rounded-2xl blur opacity-30 
        group-hover:opacity-60 transition-opacity duration-500"></div>
                    <span className="relative z-10 text-white flex items-center gap-2">
                      🎥 Watch Project Video
                    </span>
                  </a>
                </div>
              )} */}

            </motion.div>

            {/* Key Features RIGHT */}

            {project.features &&
              <motion.div
                className="bg-white/20 backdrop-blur-md rounded-2xl sm:rounded-3xl p-5 sm:p-6 lg:p-8 shadow-xl w-full max-w-full overflow-hidden"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false, amount: 0.3 }}
                variants={zoomInUp}
              >
                <h2 className="text-2xl sm:text-2xl lg:text-3xl font-bold text-slate-200 mb-4 sm:mb-6 text-center">
                  Key Features
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-3 sm:gap-4">
                  {project.features.map((feature, index) => (
                    <div
                      key={index}
                      className="flex items-start space-x-3 p-3 sm:p-4 rounded-2xl transition-colors duration-300"
                    >
                      <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 mt-0.5 flex-shrink-0 text-white" />
                      <span className="text-slate-200 text-sm sm:text-base leading-relaxed break-words hyphens-auto">
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>
            }
          </div>
        </div>
      </div>
      {/* Project Gallery */}
      <div className="bg-white rounded-3xl shadow-xl p-14 border border-slate-200/50">
        {/* Centered Heading */}
        <h3 className="text-2xl md:text-4xl font-extrabold text-gray-900 mb-2 tracking-tight text-center" style={{ fontFamily: "Merriweather, serif" }}>
          Project Gallery
        </h3>
        <span className="block w-24 sm:w-32 md:w-40 h-0.5 sm:h-1  lg:mx-auto bg-gradient-to-r from-yellow-500 via-yellow-300 to-white rounded-full mt-2 sm:mt-4 mb-8"></span>
        {/* Dynamic Status Badge */}
        {/* <div className="flex justify-center mb-6">
          <motion.p
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, type: "spring", stiffness: 120 }}
            className={`px-5 py-2 font-semibold rounded-full shadow-lg ${project.status === "Completed"
              ? "bg-slate-900 text-white"
              : "bg-yellow-400 text-black"
              }`}
          >
            {project.status === "Completed" ? " Onsite Project" : "⏳ Ongoing Project"}
          </motion.p>
        </div> */}

        {/* Gallery */}
        {project.gallery && project.gallery.length > 0 ? (
          <Swiper
            modules={[Pagination, Autoplay]}
            spaceBetween={20}
            slidesPerView={1}
            navigation
            pagination={{ clickable: true }}
            autoplay={{ delay: 3000, disableOnInteraction: false }}
            loop={true}
            breakpoints={{
              640: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
            }}
            className="rounded-2xl"
          >
            {project.gallery.map((image, index) => (
              <SwiperSlide key={index}>
                <div
                  className="group relative w-full h-64 md:h-56 lg:h-64 overflow-hidden rounded-2xl cursor-pointer transform hover:scale-105 transition-transform duration-300"
                  onClick={() => openLightbox(index, project.gallery)}
                >
                  {/* Real Image */}
                  <img
                    src={image}
                    alt={`${project.title} - Image ${index + 1}`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 border-8"
                  />

                  {/* Dynamic Overlay */}

                  {/* ExternalLink Icon on hover */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <ExternalLink className="w-8 h-8 text-white" />
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          <p className="text-center text-slate-500 font-medium">
            Gallery not available
          </p>
        )}
      </div>

      {/* {project.status === "Ongoing" && (
        <div className="bg-white rounded-3xl shadow-xl p-8 border border-slate-200/50 mt-12">
        
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">
            Current Project Status
          </h2>

          <Swiper
            modules={[Pagination, Autoplay]}
            spaceBetween={20}
            slidesPerView={1}
            navigation
            pagination={{ clickable: true }}
            autoplay={{ delay: 3000, disableOnInteraction: false }}
            breakpoints={{
              640: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
            }}
            className="rounded-2xl"
          >
            {project.currentProjectsData?.slice().reverse().map((image, index) => (
              <SwiperSlide key={index}>
                <div
                  className="group relative w-full h-64 md:h-56 lg:h-64 overflow-hidden rounded-2xl cursor-pointer transform hover:scale-105 transition-transform duration-300"
                  onClick={() => openLightbox(index, project.currentProjectsData.slice().reverse())}
                >
                  <img
                    src={image}
                    alt={`${project.title} - Image ${index + 1}`}
                    className="w-full h-full object-fill group-hover:scale-110 transition-transform duration-500 border-8 border-gray-300"
                  />
                  <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <ExternalLink className="w-8 h-8 text-white" />
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

        </div>
      )} */}


      {/* Image Lightbox */}
      <ImageLightbox
        images={lightboxImages}
        currentIndex={currentImageIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        onNext={nextImage}
        onPrev={prevImage}
        onSelect={(index) => setCurrentImageIndex(index)}
      />

      <Footer />
    </div>
  );
};

export default ProjectDetailPage;
